import { useCallback, useMemo, useState, type FC } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import type { PartnerDto } from '@/api';
import {
  Alert,
  Checkbox,
  DebouncedSearchInput,
  Select,
  Table,
  TablePagination,
  useTableSort,
  type TableColumn,
} from '@/ui';

import { resolveApiErrorMessage } from '../../lib/apiError';
import { toNumber } from '../../lib/format';
import { formatPaymentTerms } from '../../lib/paymentTerms';
import { formatSort } from '../../lib/tableSort';
import { useSetPartnerPlanning } from './api/mutations';
import { PaymentTermsDialog } from './PaymentTermsDialog';
import { useCustomersList } from './api/queries';
import {
  PAGE_SIZE,
  resolveCustomersSearch,
  type ActivityFilter,
  type CustomersSearch,
} from './lib/searchParams';

const ROUTE_ID = '/_app/customers' as const;
const ROUTE_PATH = '/customers' as const;

/*
 * Клиенты - справочник партнёров 1С с признаком «Клиент»: его наполняет поток из
 * 1С (architecture/contracts/onec-partners.md). Карточки клиента пока нет; из
 * этого же списка выбирается клиент отгрузки. С нашей стороны здесь меняются
 * наши поля: участие клиента в плане из истории (сводных «Физлицо...»
 * исключают) и условия оплаты - в 1С они не ведутся.
 */
export const CustomersPage: FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: ROUTE_ID });
  const resolved = resolveCustomersSearch(search);
  const [termsOf, setTermsOf] = useState<PartnerDto | null>(null);

  const setSearch = useCallback(
    (patch: Partial<CustomersSearch>, resetPage = true) => {
      void navigate({
        from: ROUTE_PATH,
        to: ROUTE_PATH,
        search: (previous: CustomersSearch) => ({
          ...previous,
          ...patch,
          ...(resetPage ? { page: undefined } : null),
        }),
      });
    },
    [navigate],
  );

  const { toggleSort } = useTableSort(resolved.sort, (next) =>
    setSearch({ sort: formatSort(next) }),
  );

  const { data, isPending, isError, error } = useCustomersList(resolved);
  const setPlanning = useSetPartnerPlanning();

  const total = toNumber(data?.totalRecords ?? 0);
  const totalPages = Math.max(1, toNumber(data?.totalPages ?? 1));
  const firstOnPage = total === 0 ? 0 : (resolved.page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(resolved.page * PAGE_SIZE, total);

  const columns = useMemo<ReadonlyArray<TableColumn<PartnerDto>>>(
    () => [
      {
        id: 'code',
        header: 'Код',
        mono: true,
        width: 'w-36',
        isSortable: true,
        cell: (partner) => partner.code,
      },
      {
        id: 'name',
        header: 'Наименование',
        isSortable: true,
        cell: (partner) => (
          <span className="font-[550] text-ink-900">{partner.name}</span>
        ),
      },
      {
        id: 'fullName',
        header: 'Полное наименование',
        hideBelow: 'lg',
        cell: (partner) => (
          <span className="text-ink-600">{partner.fullName ?? ''}</span>
        ),
      },
      {
        id: 'paymentTerms',
        header: 'Условия оплаты',
        width: 'w-60',
        hideBelow: 'md',
        cell: (partner) => (
          <button
            type="button"
            onClick={() => setTermsOf(partner)}
            title="Изменить условия оплаты"
            className="inline-flex max-w-full cursor-pointer items-center gap-1.5 text-left text-ink-700 hover:text-brand-green-700"
          >
            <span className="truncate">
              {formatPaymentTerms(partner) ?? (
                <span className="text-ink-400">не заданы</span>
              )}
            </span>
            <Pencil aria-hidden className="size-3.5 shrink-0 text-ink-400" />
          </button>
        ),
      },
      {
        id: 'planning',
        header: 'В планировании',
        width: 'w-36',
        align: 'right',
        cell: (partner) => (
          <Checkbox
            aria-label={`${partner.name}: участвует в плане из истории`}
            title="Снимите, чтобы «Сформировать из истории» не предлагал клиенту отгрузок"
            checked={!partner.isExcludedFromPlanning}
            disabled={setPlanning.isPending}
            onChange={(event) =>
              setPlanning.mutate({
                path: { id: partner.id },
                body: { isExcludedFromPlanning: !event.target.checked },
              })
            }
          />
        ),
      },
    ],
    [setPlanning],
  );

  return (
    <>
      <div className="mb-3.5 flex flex-wrap items-center gap-2 rounded-lg border border-ink-200 bg-white px-3.5 py-3">
        <DebouncedSearchInput
          className="min-w-[220px] flex-1"
          placeholder="Поиск по коду или наименованию..."
          aria-label="Поиск клиента"
          value={resolved.search}
          onCommit={(value) => setSearch({ search: value || undefined })}
        />
        <Select
          aria-label="Действие клиента"
          value={resolved.activity}
          onChange={(event) => {
            const activity = event.target.value as ActivityFilter;
            setSearch({
              activity: activity === 'active' ? undefined : activity,
            });
          }}
        >
          <option value="active">Действующие</option>
          <option value="inactive">Удалённые в 1С</option>
          <option value="all">Все</option>
        </Select>
      </div>

      {setPlanning.isError && (
        <Alert tone="error" className="mb-3.5">
          Не удалось изменить участие в планировании:{' '}
          {resolveApiErrorMessage(setPlanning.error)}
        </Alert>
      )}

      {isError ? (
        <Alert tone="error">
          Не удалось загрузить клиентов: {resolveApiErrorMessage(error)}.
        </Alert>
      ) : (
        <Table
          columns={columns}
          rows={data?.items ?? []}
          getRowKey={(partner) => partner.id}
          isLoading={isPending}
          sort={resolved.sort}
          onSortChange={toggleSort}
          emptyLabel={
            resolved.search === ''
              ? 'Клиентов пока нет: справочник наполняется из 1С'
              : `По запросу «${resolved.search}» ничего не найдено`
          }
          footer={
            <TablePagination
              page={resolved.page}
              totalPages={totalPages}
              onPageChange={(page) =>
                setSearch({ page: page === 1 ? undefined : page }, false)
              }
              summary={
                total === 0
                  ? 'Ничего не найдено'
                  : `Показаны ${firstOnPage}-${lastOnPage} из ${total}`
              }
            />
          }
        />
      )}

      {termsOf && (
        <PaymentTermsDialog
          partnerId={termsOf.id}
          partnerName={termsOf.name}
          terms={termsOf}
          onClose={() => setTermsOf(null)}
        />
      )}
    </>
  );
};
